// ═════════════════════════════════════════════════════════════════════════════
//  DELIVERY / PICKUP — the row the field app writes
// ═════════════════════════════════════════════════════════════════════════════
// Two ways material lands on a job, one table.
//
//   DELIVERY — ordered. The office schedules it and it walks the status list
//              (requested → on_schedule → delivered). It carries a needed-by
//              date and time, and it emails the office to ask for a truck.
//
//   PICKUP   — somebody drove to the supplier and came back with it. There is
//              nothing to schedule and nothing to ask for: by the time anyone
//              types it, it has already happened. So it is written CLOSED and
//              never enters the queue.
//
// Split out of js/delivery.js because two of the decisions here are invisible
// on screen and expensive to get wrong:
//
//  · status 'delivered' for a pickup. The material IS on the job. Every filter
//    that already hides finished deliveries hides this one too, with no change
//    to any of them.
//
//  · requested_at = the day it was collected, at local NOON. Job Costs reads
//    requested_at as a delivery's cost date and the archive splits by it, so a
//    Friday pickup typed on Monday has to cost on Friday. Noon rather than
//    midnight because midnight in a west-of-UTC timezone converts to the
//    PREVIOUS day once it is stored as UTC — the classic off-by-one that puts
//    a cost in the wrong week and, four times a year, the wrong month.
//
//   PACKING SLIP — material that arrived WITHOUT a request. The foreman sends
//              only the slip photo and the project; the office types the
//              items later. It is a pickup still 'requested' with no items:
//              nothing counts as money until it is 'delivered' (isDelivered),
//              and the foreman may still cancel it (RLS deletes 'requested').
//
// No price is built here, for any kind. Material is costed from the org's
// catalogue (delivery_material_prices) at read time, which is what lets the
// owner price an item next week and have every pickup already recorded fill
// itself in.

export function buildDeliveryRow({
  slip = false,
  pickup = false,
  projectId,
  foremanId,
  items = {},
  notes = '',
  neededBy = null,
  neededByTime = null,
  pickedUpOn = null,
  supplier = null,
} = {}) {
  if (slip) pickup = true;
  const row = {
    project_id: projectId,
    foreman_id: foremanId,
    items: slip ? {} : items,
    notes: clean(notes),
    fulfilment: pickup ? 'pickup' : 'delivery',
    // A pickup has no delivery window and a delivery has no supplier line:
    // writing the other kind's fields would leave a row that reads as both.
    needed_by: pickup ? null : clean(neededBy),
    needed_by_time: pickup ? null : clean(neededByTime),
    supplier: pickup ? clean(supplier) : null,
  };

  if (pickup) {
    row.status = slip ? 'requested' : 'delivered';
    row.requested_at = pickupTimestamp(pickedUpOn);
  }
  return row;
}

function clean(v) {
  const s = typeof v === 'string' ? v.trim() : v;
  return s === '' || s === undefined ? null : s;
}

// A slip the office has not entered yet. Anything not 'delivered' is still
// waiting (a cancelled one is not money either).
export function isPendingSlip(row) {
  return row?.fulfilment === 'pickup' && row?.status !== 'delivered';
}

// The office enters the slip: it becomes an ordinary pickup. delivered_at is
// sent explicitly — the delivery_requests_stamp_delivered_at trigger would
// otherwise stamp now(), and Job Costs dates material by delivered_at, so a
// slip entered on Monday for Friday's load would cost on Monday.
export function slipEntryPatch({ items, supplier, pickedUpOn } = {}) {
  const at = pickupTimestamp(pickedUpOn);
  return { items, supplier: clean(supplier), status: 'delivered', requested_at: at, delivered_at: at };
}

// 'YYYY-MM-DD' → an ISO timestamp at LOCAL noon that day. A blank or unparsable
// date falls back to now: the row is worth more with a slightly wrong date than
// lost to a crash, and the field never lets it through empty anyway.
export function pickupTimestamp(pickedUpOn, now = new Date()) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(pickedUpOn || ''))) return now.toISOString();
  const d = new Date(`${pickedUpOn}T12:00:00`);
  return Number.isNaN(d.getTime()) ? now.toISOString() : d.toISOString();
}

// ── No signal: the outbox record (js/offline.js) ─────────────────────────────
// A delivery or a pickup the phone could not send rides the same outbox as the
// forms. row.id is made on the phone, so the replay of a request that already
// landed meets its own primary key (23505) and stops there: one row, however
// often it is retried. The slip photos go along as blobs, like an EWO's.
export function buildDeliveryOutboxRecord({
  row, projectName, photos = [], orgId = null, userId = null, userName = '', now = new Date(),
} = {}) {
  if (!row || !row.id) throw new TypeError('buildDeliveryOutboxRecord: the row needs its id');
  const pickup = row.fulfilment === 'pickup';
  const slip = pickup && row.status === 'requested';
  return {
    queuedAt: now.getTime(),
    attempts: 0,
    stuck: false,
    waiting: null,
    orgId,
    userId,
    userName,
    syncKind: 'delivery',
    pdf: null,
    signed: false,
    submissionType: slip ? 'Packing slip' : pickup ? 'Material pickup' : 'Delivery request',
    project: projectName,
    clientUuid: row.id,
    // The time it was asked for, not the time the signal came back. A pickup
    // already carries its own day (noon of the day collected).
    delivery: { pickup, slip, projectName, row: { ...row, requested_at: row.requested_at || now.toISOString() } },
    photos: (photos || []).map(b => ({ blob: b, type: (b && b.type) || 'image/jpeg' })),
  };
}

// Where a packing slip photo lives: the caller's own {org}/{uid}/ folder (the
// only one the bucket lets them write), under the request's id, so a retry
// writes the same path and finds its own photo already there.
export function slipPhotoPath(orgId, userId, requestId, index, type) {
  return `${orgId}/${userId}/delivery-slips/${requestId}/slip-${index}.${type === 'image/png' ? 'png' : 'jpg'}`;
}

// The request's id (a uuid column). randomUUID is Safari 15.4+; older iPhones
// get the same v4 shape from getRandomValues (iOS 11+).
export function newRequestId(c = globalThis.crypto) {
  if (typeof c.randomUUID === 'function') return c.randomUUID();
  const b = c.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, x => (x < 16 ? '0' : '') + x.toString(16)).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
